import { NgStyle } from '@angular/common'
import { Component, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCamera, faVideo, faFile, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import { ListItem } from '../../../../shared'
import { IssueReport } from '../issue-report.model'

@Component({
    selector: 'app-issue-report-form',
    templateUrl: './issue-report-form.component.html',
    styleUrls: ['./issue-report-form.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle],
})
export class IssueReportFormComponent {
    issueReport = input.required<IssueReport>()
    jobOptions = input<ListItem<string>[]>([])
    employeeOptions = input<ListItem<number>[]>([])
    equipmentOptions = input<ListItem<string>[]>([])
    skillCategoryOptions = input<ListItem<string>[]>([])
    mechanicOptions = input<ListItem<number>[]>([])

    faCamera = faCamera
    faVideo = faVideo
    faFile = faFile
    faX = faX

    progress = 0

    constructor() {
        effect(() => {
            const report = this.issueReport()
            if (report.failureTypes.length) {
                this.failureTypeOptions = this.withChecked(this.failureTypeOptions, report.failureTypes)
            }
            if (report.quickObservations.length) {
                this.quickObservationOptions = this.withChecked(this.quickObservationOptions, report.quickObservations)
            }
            if (report.actionsTaken.length) {
                this.actionTakenOptions = this.withChecked(this.actionTakenOptions, report.actionsTaken)
            }
        })
    }

    private withChecked(options: ListItem<string>[], selected: string[]): ListItem<string>[] {
        return options.map(opt => {
            const item = new ListItem(opt.value, opt.display)
            item.$checked = selected.includes(opt.value)
            return item
        })
    }

    multiSelectHeaderFormatter = (ms: any) => {
        const checked = ms.checkedItems
        if (!checked || checked.length === 0) return ''
        return checked.map((item: any) => item.display ?? item).join(', ')
    }

    failureTypeOptions: ListItem<string>[] = [
        new ListItem('No start', 'No start'),
        new ListItem('Stalled / shut down', 'Stalled / shut down'),
        new ListItem('Loss of power', 'Loss of power'),
        new ListItem('Leak (oil / fuel / hydraulic / coolant)', 'Leak (oil / fuel / hydraulic / coolant)'),
        new ListItem('Warning light / alarm', 'Warning light / alarm'),
        new ListItem('Noise (grinding / knocking / squeal)', 'Noise (grinding / knocking / squeal)'),
        new ListItem('Vibration', 'Vibration'),
        new ListItem('Overheating', 'Overheating'),
        new ListItem('Slow / weak function', 'Slow / weak function'),
        new ListItem('Electrical issue', 'Electrical issue'),
        new ListItem('Flat tire', 'Flat tire'),
        new ListItem('Other', 'Other'),
    ]

    conditionNowOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('Will NOT run / move', 'Will NOT run / move'),
        new ListItem('Runs but unsafe', 'Runs but unsafe'),
        new ListItem('Runs with reduced performance', 'Runs with reduced performance'),
        new ListItem('Intermittent issue', 'Intermittent issue'),
    ]

    safetyStatusOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('SAFE to operate', 'SAFE to operate'),
        new ListItem('NOT SAFE – OUT OF SERVICE', 'NOT SAFE – OUT OF SERVICE'),
    ]

    quickObservationOptions: ListItem<string>[] = [
        new ListItem('Visible leak', 'Visible leak'),
        new ListItem('Smoke', 'Smoke'),
        new ListItem('Burning smell', 'Burning smell'),
        new ListItem('Damaged hose / wire', 'Damaged hose / wire'),
        new ListItem('Loose / broken part', 'Loose / broken part'),
        new ListItem('Fluid low', 'Fluid low'),
        new ListItem('No visible issue', 'No visible issue'),
    ]

    actionTakenOptions: ListItem<string>[] = [
        new ListItem('Shut down', 'Shut down'),
        new ListItem('Parked safely', 'Parked safely'),
        new ListItem('Tagged out', 'Tagged out'),
        new ListItem('Supervisor notified', 'Supervisor notified'),
    ]

    severityOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('Low (monitor / minor)', 'Low (monitor / minor)'),
        new ListItem('Medium (needs repair soon)', 'Medium (needs repair soon)'),
        new ListItem('High (down equipment)', 'High (down equipment)'),
        new ListItem('Critical (safety hazard)', 'Critical (safety hazard)'),
    ]

    onFailureTypesChanged() {
        this.issueReport().failureTypes = this.failureTypeOptions
            .filter(x => x.$checked)
            .map(x => x.value)
    }

    onQuickObservationsChanged() {
        this.issueReport().quickObservations = this.quickObservationOptions
            .filter(x => x.$checked)
            .map(x => x.value)
    }

    onActionsTakenChanged() {
        this.issueReport().actionsTaken = this.actionTakenOptions
            .filter(x => x.$checked)
            .map(x => x.value)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = input.files
        const progress = (p: number) => {
            this.progress = p
        }
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.issueReport().images.push(compressedFile)
        }
        input.value = ''
    }

    onVideoChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = input.files
        for (let i = 0; i < files.length; i++) {
            const video = files[i]
            const reader = new FileReader()
            reader.onload = e => {
                const dataURL = e.target?.result as string
                ;(video as any).previewSrc = dataURL
            }
            reader.readAsDataURL(video)
            this.issueReport().videos.push(video)
        }
        input.value = ''
    }

    onDocumentChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = input.files
        for (let i = 0; i < files.length; i++) {
            this.issueReport().documents.push(files[i])
        }
        input.value = ''
    }

    removeImage(image: any) {
        const idx = this.issueReport().images.indexOf(image)
        if (idx > -1) this.issueReport().images.splice(idx, 1)
    }

    removeVideo(video: any) {
        const idx = this.issueReport().videos.indexOf(video)
        if (idx > -1) this.issueReport().videos.splice(idx, 1)
    }

    removeDocument(doc: any) {
        const idx = this.issueReport().documents.indexOf(doc)
        if (idx > -1) this.issueReport().documents.splice(idx, 1)
    }

    private static readonly VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi']

    isVideo(url: string): boolean {
        const lower = url.toLowerCase()
        return IssueReportFormComponent.VIDEO_EXTENSIONS.some(ext => lower.endsWith(ext))
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    getExistingFileSrc(url: string) {
        return './Resources/Images/EquipmentReportIssue/' + url
    }

    openImage(url: string) {
        window.open('./Resources/Images/EquipmentReportIssue/' + url, '_blank')
    }
}
